import { useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useProfile";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface ProfileAvatarProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

const ProfileAvatar = ({ size = "md", className = "" }: ProfileAvatarProps) => {
  const { profile, isLoading, error, refetchProfile } = useProfile();
  const { user, session } = useAuth();
  
  const getSizeClass = () => {
    switch (size) {
      case "sm": return "h-8 w-8";
      case "lg": return "h-12 w-12";
      default: return "h-10 w-10";
    }
  };
  
  // Get user's initials or fallback to a default
  const getInitials = () => {
    // Try to get initials from profile first
    if (profile?.first_name || profile?.last_name) {
      const firstInitial = profile.first_name?.[0] || '';
      const lastInitial = profile.last_name?.[0] || '';
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }
    
    // If no profile name is set, use the first letter of their email if available
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    
    // Last resort fallback
    return 'U';
  };

  // Auto-retry profile fetch when session becomes available or changes
  useEffect(() => {
    if (session && error && !isLoading && refetchProfile) {
      // Wait a moment then retry to ensure session is fully refreshed
      const retryTimer = setTimeout(() => {
        refetchProfile();
      }, 500);
      
      return () => clearTimeout(retryTimer);
    }
  }, [session?.access_token, error, isLoading, refetchProfile]); // Use session.access_token instead of full session object
  
  // Show loading skeleton during initial load
  if (isLoading && !profile) {
    return (
      <Skeleton className={`${getSizeClass()} rounded-full ${className}`} />
    );
  }
  
  // For error states, show fallback without spinning loader
  // This prevents the indefinite spinning when there are session issues
  if (error || !profile) {
    return (
      <Avatar className={`${getSizeClass()} border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 ${className}`}>
        <AvatarFallback className="bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 font-semibold">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
    );
  }
  
  return (
    <Avatar className={`${getSizeClass()} border border-gray-300 dark:border-gray-600 ${className}`}>
      <AvatarImage 
        src={profile.avatar_url || ""} 
        alt={`${profile.first_name || ''} ${profile.last_name || ''}`}
      />
      <AvatarFallback className="bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 font-semibold">
        {getInitials()}
      </AvatarFallback>
    </Avatar>
  );
};

export default ProfileAvatar; 